import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { parkingApi, usePkList, usePkMutation, type PkRate, type PkValidation } from "../api/parkingApi";
import { ConsolePage, LoadState, Panel, StatusChip, ToolButton, formatDateTime } from "../components/console/Console";
import FormModal, { type FormField, type FormValues } from "../components/console/FormModal";
import { errorText, money } from "./parkingUi";

// ===========================================================================
// Control de estacionamientos → Tarifas: tarifas editables (primer periodo,
// fracción, tolerancia, tope diario, nocturna, boleto perdido) y validaciones
// de comercios (minutos libres, porcentaje, monto fijo o cortesía). El cobro
// las aplica en el servidor.
// ===========================================================================

export default function RatesScreen() {
  const { t, i18n } = useTranslation("parking");
  const lotsQ = usePkList("lots");
  const ratesQ = usePkList("rates");
  const valsQ = usePkList("validations");
  const [rate, setRate] = useState<Partial<PkRate> | null>(null);
  const [val, setVal] = useState<Partial<PkValidation> | null>(null);
  const lots = lotsQ.data ?? [];
  const lotName = (id: string) => lots.find((l) => l.id === id)?.name ?? id;

  const saveRate = usePkMutation(({ id, body }: { id?: string; body: Partial<PkRate> }) => (id ? parkingApi.update("rates", id, body) : parkingApi.create("rates", body)));
  const saveVal = usePkMutation(({ id, body }: { id?: string; body: Partial<PkValidation> }) => (id ? parkingApi.update("validations", id, body) : parkingApi.create("validations", body)));
  const removeVal = usePkMutation((id: string) => parkingApi.remove("validations", id));

  const lotOptions = lots.map((l) => ({ value: l.id, label: l.name }));
  const rateFields: FormField[] = [
    { key: "name", label: t("rates.name"), required: true, full: true },
    { key: "vehicle_type", label: t("occupancy.vehicleType"), type: "select", required: true, options: (["car", "motorcycle", "truck"] as const).map((v) => ({ value: v, label: t(`vehicle.${v}`) })) },
    { key: "grace_minutes", label: t("rates.grace"), type: "number", min: 0 },
    { key: "first_period_minutes", label: t("rates.firstMinutes"), type: "number", min: 1, required: true },
    { key: "first_period_price", label: t("rates.firstPrice"), type: "number", min: 0, required: true },
    { key: "fraction_minutes", label: t("rates.fractionMinutes"), type: "number", min: 1, required: true },
    { key: "fraction_price", label: t("rates.fractionPrice"), type: "number", min: 0, required: true },
    { key: "daily_cap", label: t("rates.dailyCap"), type: "number", min: 0, hint: t("rates.dailyCapHint") },
    { key: "lost_ticket_fee", label: t("rates.lostFee"), type: "number", min: 0 },
    { key: "night_enabled", label: t("rates.nightEnabled"), type: "checkbox" },
    { key: "night_price", label: t("rates.nightPrice"), type: "number", min: 0 },
    { key: "night_start", label: t("rates.nightStart"), placeholder: "22:00" },
    { key: "night_end", label: t("rates.nightEnd"), placeholder: "07:00" },
    { key: "lot_ids", label: t("rates.lots"), type: "multiselect", options: lotOptions, hint: t("rates.lotsHint") },
    { key: "active", label: t("rates.active"), type: "checkbox" },
    { key: "is_default", label: t("rates.isDefault"), type: "checkbox" },
    { key: "description", label: t("rates.description"), type: "textarea" },
  ];
  const valFields: FormField[] = [
    { key: "merchant_name", label: t("validations.merchant"), required: true, full: true },
    { key: "code", label: t("validations.code"), required: true, placeholder: "COMERCIO-2H", hint: t("validations.codeHint") },
    { key: "type", label: t("validations.type"), type: "select", required: true, options: (["free_minutes", "percent", "fixed", "free"] as const).map((v) => ({ value: v, label: t(`validationType.${v}`) })) },
    { key: "value", label: t("validations.value"), type: "number", min: 0 },
    { key: "max_uses", label: t("validations.maxUses"), type: "number", min: 0, hint: t("validations.maxUsesHint") },
    { key: "lot_ids", label: t("rates.lots"), type: "multiselect", options: lotOptions },
    { key: "active", label: t("rates.active"), type: "checkbox" },
  ];

  const submitRate = async (v: FormValues) => {
    try {
      await saveRate.mutateAsync({ id: rate?.id, body: { ...v, night_start: String(v.night_start || "22:00"), night_end: String(v.night_end || "07:00") } as Partial<PkRate> });
      toast.success(t("common.saved"));
      setRate(null);
    } catch (err) {
      toast.error(errorText(err, t("common.saveError")));
    }
  };
  const submitVal = async (v: FormValues) => {
    try {
      await saveVal.mutateAsync({ id: val?.id, body: { ...v, code: String(v.code || "").toUpperCase() } as Partial<PkValidation> });
      toast.success(t("common.saved"));
      setVal(null);
    } catch (err) {
      toast.error(errorText(err, t("common.saveError")));
    }
  };

  const valueText = (v: PkValidation) =>
    v.type === "free_minutes" ? t("validations.freeMinutesN", { interpolation: { escapeValue: false }, count: v.value }) : v.type === "percent" ? `${v.value} %` : v.type === "fixed" ? money(v.value, i18n.language) : t("validationType.free");

  return (
    <ConsolePage
      icon="ri-price-tag-3-line"
      title={t("rates.title")}
      subtitle={t("rates.subtitle")}
      actions={
        <>
          <ToolButton icon="ri-coupon-3-line" onClick={() => setVal({ type: "free_minutes", value: 60, active: true, lot_ids: [], max_uses: 0 })}>{t("validations.new")}</ToolButton>
          <ToolButton icon="ri-add-line" variant="primary" onClick={() => setRate({ vehicle_type: "car", first_period_minutes: 60, first_period_price: 30, fraction_minutes: 15, fraction_price: 8, grace_minutes: 15, daily_cap: 220, lost_ticket_fee: 250, night_enabled: false, night_start: "22:00", night_end: "07:00", night_price: 0, active: true, is_default: false, lot_ids: [] })}>{t("rates.new")}</ToolButton>
        </>
      }
    >
      <LoadState loading={ratesQ.isLoading} error={ratesQ.error} empty={!ratesQ.data?.length} emptyText={t("rates.empty")} onRetry={() => ratesQ.refetch()}>
        <div className="grid gap-3 md:grid-cols-2 2xl:grid-cols-3">
          {(ratesQ.data ?? []).map((r) => (
            <article key={r.id} className={`rounded-xl border bg-card p-4 ${r.active ? "border-line" : "border-dashed border-line opacity-70"}`}>
              <header className="mb-3 flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="truncate text-sm font-semibold text-content">{r.name}</h3>
                  <p className="truncate text-xs text-content-secondary">{r.lot_ids.length ? r.lot_ids.map(lotName).join(" · ") : t("rates.allLots")}</p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <StatusChip tone={r.active ? "success" : "neutral"}>{r.active ? t("rates.activeChip") : t("rates.inactiveChip")}</StatusChip>
                  {r.is_default && <StatusChip tone="primary">{t("rates.defaultChip")}</StatusChip>}
                </div>
              </header>
              <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                <dt className="text-content-secondary">{t("rates.firstPeriod", { interpolation: { escapeValue: false }, minutes: r.first_period_minutes })}</dt>
                <dd className="text-right font-semibold tabular-nums text-content">{money(r.first_period_price, i18n.language)}</dd>
                <dt className="text-content-secondary">{t("rates.eachFraction", { interpolation: { escapeValue: false }, minutes: r.fraction_minutes })}</dt>
                <dd className="text-right font-semibold tabular-nums text-content">{money(r.fraction_price, i18n.language)}</dd>
                <dt className="text-content-secondary">{t("rates.grace")}</dt>
                <dd className="text-right tabular-nums text-content">{r.grace_minutes} min</dd>
                <dt className="text-content-secondary">{t("rates.dailyCap")}</dt>
                <dd className="text-right tabular-nums text-content">{r.daily_cap ? money(r.daily_cap, i18n.language) : "—"}</dd>
                <dt className="text-content-secondary">{t("rates.night")}</dt>
                <dd className="text-right tabular-nums text-content">{r.night_enabled ? `${money(r.night_price, i18n.language)} (${r.night_start}–${r.night_end})` : "—"}</dd>
                <dt className="text-content-secondary">{t("rates.lostFee")}</dt>
                <dd className="text-right tabular-nums text-content">{money(r.lost_ticket_fee, i18n.language)}</dd>
              </dl>
              {r.description && <p className="mt-2 text-[11px] text-content-muted">{r.description}</p>}
              <footer className="mt-3 flex justify-between text-[11px] text-content-muted">
                <span><i className={r.vehicle_type === "motorcycle" ? "ri-motorbike-line" : r.vehicle_type === "truck" ? "ri-truck-line" : "ri-car-line"} /> {t(`vehicle.${r.vehicle_type}`)}</span>
                <button type="button" onClick={() => setRate(r)} className="font-medium text-primary-500 hover:underline">{t("common.edit")}</button>
              </footer>
            </article>
          ))}
        </div>
      </LoadState>

      <Panel title={t("validations.title")} icon="ri-coupon-3-line" bodyClassName="overflow-x-auto">
        <LoadState loading={valsQ.isLoading} error={valsQ.error} empty={!valsQ.data?.length} emptyText={t("validations.empty")}>
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-subtle text-left text-[11px] uppercase tracking-wide text-content-muted">
              <tr>
                <th className="px-3 py-2">{t("validations.merchant")}</th>
                <th className="px-3 py-2">{t("validations.code")}</th>
                <th className="px-3 py-2">{t("validations.benefit")}</th>
                <th className="px-3 py-2">{t("rates.lots")}</th>
                <th className="px-3 py-2 text-right">{t("validations.uses")}</th>
                <th className="px-3 py-2">{t("validations.validUntil")}</th>
                <th className="px-3 py-2">{t("common.status")}</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {(valsQ.data ?? []).map((v) => {
                const expired = v.valid_until && Date.parse(v.valid_until) < Date.now();
                return (
                  <tr key={v.id}>
                    <td className="px-3 py-2 font-medium text-content">{v.merchant_name}</td>
                    <td className="px-3 py-2 font-mono text-xs text-content">{v.code}</td>
                    <td className="px-3 py-2 text-content-secondary">{valueText(v)}</td>
                    <td className="max-w-[220px] truncate px-3 py-2 text-content-secondary">{v.lot_ids.length ? v.lot_ids.map(lotName).join(", ") : t("rates.allLots")}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-content">{v.uses_count ?? 0}{v.max_uses ? ` / ${v.max_uses}` : ""}</td>
                    <td className="px-3 py-2 text-content-secondary">{v.valid_until ? formatDateTime(v.valid_until, i18n.language) : "—"}</td>
                    <td className="px-3 py-2"><StatusChip tone={!v.active ? "neutral" : expired ? "error" : "success"}>{!v.active ? t("rates.inactiveChip") : expired ? t("validations.expired") : t("rates.activeChip")}</StatusChip></td>
                    <td className="whitespace-nowrap px-3 py-2 text-right">
                      <button type="button" onClick={() => setVal(v)} className="mr-2 text-xs text-primary-500 hover:underline">{t("common.edit")}</button>
                      <button
                        type="button"
                        onClick={async () => {
                          if (!window.confirm(t("common.confirmDelete", { interpolation: { escapeValue: false }, name: v.merchant_name }))) return;
                          try {
                            await removeVal.mutateAsync(v.id);
                            toast.success(t("common.deleted"));
                          } catch (err) {
                            toast.error(errorText(err, t("common.saveError")));
                          }
                        }}
                        className="text-xs text-error hover:underline"
                      >
                        {t("common.delete")}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </LoadState>
      </Panel>

      {rate && (
        <FormModal
          title={rate.id ? t("rates.edit", { interpolation: { escapeValue: false }, name: rate.name }) : t("rates.new")}
          submitLabel={t("common.save")}
          busy={saveRate.isPending}
          fields={rateFields}
          initial={rate as unknown as FormValues}
          onClose={() => setRate(null)}
          onSubmit={submitRate}
        />
      )}
      {val && (
        <FormModal
          title={val.id ? t("validations.edit", { interpolation: { escapeValue: false }, name: val.merchant_name }) : t("validations.new")}
          submitLabel={t("common.save")}
          busy={saveVal.isPending}
          fields={valFields}
          initial={val as unknown as FormValues}
          onClose={() => setVal(null)}
          onSubmit={submitVal}
        />
      )}
    </ConsolePage>
  );
}
